/** Экран результата: P₀/P₁/Δ, состав, прогнозы, ошибки, выплаты, комиссия по кругам, проверка обязательства. */
import { useEffect, useState } from 'react';
import type { GameView, SettlementView } from '@mt/protocol';
import { cr, pts } from '../lib/format';
import { verifyCommitment } from '../lib/verify';
import { downloadPackage, fetchPackage } from '../lib/verification';
import { client } from '../net/client';
import type { SignedPackage } from '@mt/verification';
import { VerifyDialog } from './VerifyDialog';
import { num, t, tm } from '../i18n';

/** Вес доли в банке приходит строкой с точкой ('0.5'); показываем с десятичным разделителем языка. */
const localWeight = (w: string) => w.replace('.', num(1.5, 1).charAt(1));

export function Settlement({ game, s }: { game: GameView; s: SettlementView }) {
  const [verified, setVerified] = useState<boolean | null>(null);
  const [showProof, setShowProof] = useState(false);
  useEffect(() => {
    if (s.commitment) void verifyCommitment(game.commitment.hash, s.commitment.salt, s.commitment.payload).then(setVerified);
  }, [s.commitment, game.commitment.hash]);
  const me = s.players.find((p) => game.seats.find((x) => x.isMe)?.playerId === p.playerId);
  const names = new Map(s.players.map((p) => [p.playerId, p.name]));
  const winners = s.players.filter((p) => p.payout > 0);
  return (
    <section className="settlement" aria-label={t('table.settle.aria')}>
      <header className={`result-head ${s.status}`}>
        <div>
          <h2>{s.status === 'cancelled' ? t('table.settle.cancelled') : s.kind === 'walkover' ? t('table.settle.walkover') : t('table.settle.title')}</h2>
          {(s.reasonMsg || s.reason) && (
            <p className="dim">
              {tm(s.reasonMsg, s.reason ?? '')}
              {s.status === 'cancelled' ? ' · ' + t('table.settle.refundedNote') : ''}
            </p>
          )}
        </div>
        <dl className="stat-grid">
          <div>
            <dt>P₀</dt>
            <dd className="mono">{pts(s.p0)}</dd>
          </div>
          <div>
            <dt>P₁</dt>
            <dd className="mono">{pts(s.p1)}</dd>
          </div>
          <div>
            <dt>Δ</dt>
            <dd className={`mono ${s.delta && s.delta > 0 ? 'up' : s.delta && s.delta < 0 ? 'down' : ''}`}>{pts(s.delta, true)}</dd>
          </div>
          {me && (
            <div>
              <dt>{t('table.settle.myNet')}</dt>
              <dd className={`mono ${me.net > 0 ? 'up' : me.net < 0 ? 'down' : ''}`}>{me.net > 0 ? '+' : ''}{cr(me.net)}</dd>
            </div>
          )}
        </dl>
      </header>

      <VerificationBand game={game} s={s} verified={verified} showProof={showProof} setShowProof={setShowProof} />

      <div className="composition">
        {t('table.settle.composition')}{' '}
        {s.composition.map((c) => (
          <span key={c.symbol} className="ticker">
            <b>{c.symbol}</b> {c.name}
          </span>
        ))}
      </div>

      <table className="grid-table">
        <thead>
          <tr>
            <th>{t('table.settle.colPlayer')}</th>
            <th>{t('table.settle.colForecast')}</th>
            <th className="num">{t('table.settle.colError')}</th>
            <th className="num">{t('table.settle.colContributed')}</th>
            <th className="num">{t('table.settle.colPayout')}</th>
            <th className="num">{t('table.settle.colRefund')}</th>
            <th className="num">{t('table.settle.colNet')}</th>
          </tr>
        </thead>
        <tbody>
          {s.players.map((p) => (
            <tr key={p.playerId} className={`${p.payout > 0 ? 'win' : ''} ${p.playerId === me?.playerId ? 'me' : ''}`}>
              <td>
                {p.name}
                {p.folded && <em className="tag">{t('table.settle.folded')}</em>}
              </td>
              <td className={p.direction === 'up' ? 'up' : p.direction === 'down' ? 'down' : ''}>
                {p.direction === 'up' ? '▲' : p.direction === 'down' ? '▼' : '—'} {pts(p.points)}
                {s.kind === 'market' && !p.folded && !p.correctDirection && <em className="tag">{t('table.settle.wrongDirection')}</em>}
              </td>
              <td className="num mono">{p.error === null ? '—' : pts(p.error)}</td>
              <td className="num mono">{cr(p.contributed)}</td>
              <td className="num mono">{p.payout ? cr(p.payout) : '—'}</td>
              <td className="num mono">{p.refund ? cr(p.refund) : '—'}</td>
              <td className={`num mono ${p.net > 0 ? 'up' : p.net < 0 ? 'down' : ''}`}>{p.net > 0 ? '+' : ''}{cr(p.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="settle-cols">
        <div>
          <h3>{t('table.settle.pots')}</h3>
          <table className="grid-table compact">
            <thead>
              <tr>
                <th>{t('table.settle.colPot')}</th>
                <th className="num">{t('table.settle.colAmount')}</th>
                <th className="num">{t('table.settle.colCommission')}</th>
                <th>{t('table.settle.colRecipients')}</th>
              </tr>
            </thead>
            <tbody>
              {s.pots.map((p) => (
                <tr key={p.index}>
                  <td>{p.index === 0 ? t('table.settle.mainPot') : t('table.settle.sidePot', { n: num(p.index, 0) })}</td>
                  <td className="num mono">{cr(p.amount)}</td>
                  <td className="num mono">{p.refunded ? '—' : cr(p.commission)}</td>
                  <td>
                    {p.refunded ? (
                      <span className="dim">{t('table.settle.potRefunded')}</span>
                    ) : (
                      <ul className="share-list">
                        {p.shares.map((sh) => (
                          <li key={sh.playerId}>
                            <span>{names.get(sh.playerId)}</span>
                            <span className="mono">{cr(sh.amount)}</span>
                            {sh.weight !== '—' && <span className="mono dim">{localWeight(sh.weight)}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h3>{t('table.settle.commissionByRound')}</h3>
          <table className="grid-table compact">
            <thead>
              <tr>
                <th>{t('table.settle.colRound')}</th>
                <th className="num">{t('table.settle.colNewContributions')}</th>
                <th className="num">{t('table.settle.colCommission')}</th>
              </tr>
            </thead>
            <tbody>
              {s.commissionByRound.map((r) => (
                <tr key={r.round}>
                  <td>
                    {r.round}
                    {r.round === 1 && <span className="dim"> · {t('table.settle.withAntes')}</span>}
                  </td>
                  <td className="num mono">{cr(r.newContributions)}</td>
                  <td className="num mono">
                    {cr(r.commission)}
                    {r.cap !== null && r.commission === r.cap && r.commission > 0 && <em className="tag">{t('table.settle.cap')}</em>}
                  </td>
                </tr>
              ))}
              <tr className="total">
                <td colSpan={2}>{t('table.settle.toHouse')}</td>
                <td className="num mono">{cr(s.bankDelta)}</td>
              </tr>
            </tbody>
          </table>
          <p className={`small ${s.totals.balanced ? 'ok' : 'down'}`}>
            {t('table.settle.reconciliation', { debited: s.totals.debited, paid: s.totals.paid, refunded: s.totals.refunded, bank: s.totals.bank })} {s.totals.balanced ? '✓' : '✗'}
          </p>
        </div>
      </div>

      {winners.length === 0 && s.status === 'completed' && <p className="dim">{t('table.settle.noWinners')}</p>}
    </section>
  );
}

/** Полоса проверяемости: обязательство, пакет проверки, проверка в браузере. */
function VerificationBand(props: { game: GameView; s: SettlementView; verified: boolean | null; showProof: boolean; setShowProof: (f: (v: boolean) => boolean) => void }) {
  const { game, s, verified } = props;
  const [dialog, setDialog] = useState<SignedPackage | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<SignedPackage>) => {
    setBusy(true);
    try {
      setDialog(await fn());
    } catch (e) {
      client.toast('error', t('table.verify.packageError', { error: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="verify-band">
      <div className="verify-band-main">
        <span className="verify-band-title">{t('table.verify.title')}</span>
        <span className="muted-line">
          {t('table.verify.commitmentPre')} <code>{game.commitment.hash.slice(0, 16)}…</code> {t('table.verify.commitmentPost')}{' '}
          {verified === true ? (
            <b className="ok">{t('table.verify.match')}</b>
          ) : verified === false ? (
            <b className="down">{t('table.verify.mismatch')}</b>
          ) : s.commitment ? (
            t('table.verify.checking')
          ) : (
            t('table.verify.notRevealed')
          )}
        </span>
      </div>
      <div className="btn-row">
        {s.commitment && (
          <button className="btn tiny ghost" onClick={() => props.setShowProof((v) => !v)}>
            {props.showProof ? t('table.verify.hideSalt') : t('table.verify.showSalt')}
          </button>
        )}
        {game.verificationAvailable && (
          <>
            <button className="btn tiny" disabled={busy} onClick={() => void run(() => fetchPackage(game.gameId).then((r) => r.signed))}>
              {t('table.verify.inBrowser')}
            </button>
            <button className="btn tiny primary" disabled={busy} onClick={() => void run(() => downloadPackage(game.gameId))}>
              {t('table.verify.download')}
            </button>
          </>
        )}
      </div>
      {props.showProof && s.commitment && (
        <pre className="proof-body">{`hash = SHA-256(salt + ":" + payload)
salt = ${s.commitment.salt}
payload = ${JSON.stringify(JSON.parse(s.commitment.payload), null, 2)}`}</pre>
      )}
      {dialog !== undefined && <VerifyDialog initial={dialog} onClose={() => setDialog(undefined)} />}
    </div>
  );
}
